import type { Bundle, Reference, Task } from '@medplum/fhirtypes'
import type { ExtendedResource } from '@seen/core/ExtendedResource'
import { introducedQueuedEditsOriginTask } from '@seen/core/queued-edits/origin'
import { sortBy } from 'lodash-es'
import { useCallback, useMemo } from 'react'

// Generic helper for activity feeds: given a resource's version history (in any
// order), returns a lookup that maps a specific version to the queued-edits
// review task it originated from — but only for the version that actually
// applied the change, not the later versions that merely inherited the origin
// tag. Returns undefined for everything else.
//
// Usage in any feed (one line at the resource's ActivityEntry):
//
//   const originOf = useVersionOriginTask(versions)
//   <ActivityEntry ... originTask={originOf(entry.resourceVersion)} />
//
// Keeps origin-backlink behavior uniform across feeds without each feed
// re-implementing the prev/current tag comparison. The history is sorted by
// lastUpdated internally, so callers need not pass a pre-sorted list.
export function useVersionOriginTask<T extends ExtendedResource>(
  versions: readonly T[]
): (version: T | undefined) => Reference<Task | Bundle> | undefined {
  // Several callers pass an unmemoized `sortBy(...)` result, so `versions` is a
  // fresh array every render. Depend on a content key instead of the reference
  // so the pairing map only recomputes when the underlying versions change.
  const versionsKey = versions
    .map((v) => `${v.id}/${v.versionId}/${v.lastUpdated}`)
    .join(',')

  // Map each version (keyed by `id/versionId`) to the version that chronologically
  // precedes it *for the same resource id*. Grouping by id keeps the pairing
  // correct even when the list mixes multiple resources' histories (bulk feeds).
  const previousByVersionKey = useMemo(() => {
    const safeTime = (v: T): number => {
      try {
        return new Date(v.lastUpdated).getTime()
      } catch {
        return 0
      }
    }
    const key = (v: T): string | undefined => {
      try {
        return `${v.id}/${v.versionId}`
      } catch {
        return undefined
      }
    }
    const byId = new Map<string, T[]>()
    for (const version of versions) {
      const id = version.id
      if (!id) {
        continue
      }
      byId.set(id, [...(byId.get(id) ?? []), version])
    }
    const map = new Map<string, T | undefined>()
    for (const group of byId.values()) {
      const sorted = sortBy(group, safeTime)
      sorted.forEach((version, index) => {
        const k = key(version)
        if (k) {
          map.set(k, index > 0 ? sorted[index - 1] : undefined)
        }
      })
    }
    return map
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed by content (versionsKey), not the array reference
  }, [versionsKey])

  return useCallback(
    (version: T | undefined): Reference<Task | Bundle> | undefined => {
      if (!version) {
        return undefined
      }
      let k: string | undefined
      try {
        k = `${version.id}/${version.versionId}`
      } catch {
        return undefined
      }
      if (!previousByVersionKey.has(k)) {
        return undefined
      }
      return introducedQueuedEditsOriginTask(
        version,
        previousByVersionKey.get(k)
      )
    },
    [previousByVersionKey]
  )
}
